"use client";

import { Box, Separator, Text } from "@radix-ui/themes";
import clsx from "clsx";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useCallback } from "react";
import {
    CallLogsIcon,
    ContactEmergencyIcon,
    DotsPadIcon,
    HistoryIcon,
    MoreCircleIconBold,
    PreferenceIcon,
    SettingsIcon,
    VoiceFeaturesIcon,
    VoiceMailIcon,
} from "@/public/index";
import { checkStoredBusinessAttendantCapability, checkStoredScheduleCapability } from "@/stores/CapabilitiesStore";

interface NavBarLinks {
    href: string;
    label: string;
    icon: React.ReactElement;
    testId: string;
}

const MENU_ITEM = {
    VOICE_FEATURES: "Voice Settings",
    BUSINESS_ATTENDANT: "Business Attendant",
    SCHEDULES: "Schedules",
    CALL_HISTORY: "Call History",
    AUDIT_LOG: "Audit Log",
    ADDRESS_911: "Manage E911",
    CALLER_INFO: "Caller Info",
    VOICEMAIL: "Voicemail",
    SETTINGS: "Account Settings",
};

const MENU_URLS = {
    VOICE_FEATURES: "/voice-settings",
    BUSINESS_ATTENDANT: "/business-attendant",
    SCHEDULES: "/schedules",
    CALL_HISTORY: "/call-history",
    AUDIT_LOG: "/audit-logs",
    ADDRESS_911: "/e911",
    CALLER_INFO: "/caller-info",
    VOICEMAIL: "/voicemail",
    SETTINGS: "/account-settings",
};

export default function Navbar() {
    const hasBusinessAttendant = checkStoredBusinessAttendantCapability();
    const hasSchedule = checkStoredScheduleCapability();
    const pathname: string = usePathname();

    const navItems: NavBarLinks[][] = [
        [
            {
                href: MENU_URLS.VOICE_FEATURES,
                label: MENU_ITEM.VOICE_FEATURES,
                icon: <VoiceFeaturesIcon />,
                testId: "voice-settings",
            },
            ...(hasSchedule
                ? [
                      {
                          href: MENU_URLS.SCHEDULES,
                          label: MENU_ITEM.SCHEDULES,
                          icon: <PreferenceIcon width={24} />,
                          testId: "schedules",
                      },
                  ]
                : []),
            ...(hasBusinessAttendant
                ? [
                      {
                          href: MENU_URLS.BUSINESS_ATTENDANT,
                          label: MENU_ITEM.BUSINESS_ATTENDANT,
                          icon: <DotsPadIcon />,
                          testId: "business-attendant",
                      },
                  ]
                : []),
            {
                href: MENU_URLS.CALL_HISTORY,
                label: MENU_ITEM.CALL_HISTORY,
                icon: <HistoryIcon />,
                testId: "call-log",
            },
            {
                href: MENU_URLS.AUDIT_LOG,
                label: MENU_ITEM.AUDIT_LOG,
                icon: <CallLogsIcon />,
                testId: "audit-trail",
            },
            {
                href: MENU_URLS.ADDRESS_911,
                label: MENU_ITEM.ADDRESS_911,
                icon: <ContactEmergencyIcon />,
                testId: "e911",
            },
            {
                href: MENU_URLS.CALLER_INFO,
                label: MENU_ITEM.CALLER_INFO,
                icon: <MoreCircleIconBold />,
                testId: "caller-info",
            },
            {
                href: MENU_URLS.VOICEMAIL,
                label: MENU_ITEM.VOICEMAIL,
                icon: <VoiceMailIcon />,
                testId: "voicemail",
            },
        ],
        [
            {
                href: MENU_URLS.SETTINGS,
                label: MENU_ITEM.SETTINGS,
                icon: <SettingsIcon />,
                testId: "account-settings",
            },
        ],
    ];

    const getActiveNavText = useCallback(() => {
        switch (true) {
            case pathname.includes(MENU_URLS.VOICE_FEATURES):
                return MENU_ITEM.VOICE_FEATURES;
            case pathname.includes(MENU_URLS.BUSINESS_ATTENDANT) && hasBusinessAttendant:
                return MENU_ITEM.BUSINESS_ATTENDANT;
            case pathname.includes(MENU_URLS.SCHEDULES):
                return MENU_ITEM.SCHEDULES;
            case pathname.includes(MENU_URLS.CALL_HISTORY):
                return MENU_ITEM.CALL_HISTORY;
            case pathname.includes(MENU_URLS.AUDIT_LOG):
                return MENU_ITEM.AUDIT_LOG;
            case pathname.includes(MENU_URLS.ADDRESS_911):
                return MENU_ITEM.ADDRESS_911;
            case pathname.includes(MENU_URLS.CALLER_INFO):
                return MENU_ITEM.CALLER_INFO;
            case pathname.includes(MENU_URLS.VOICEMAIL):
                return MENU_ITEM.VOICEMAIL;
            case pathname.includes(MENU_URLS.SETTINGS):
                return MENU_ITEM.SETTINGS;
            default:
                return "";
        }
    }, [pathname, hasBusinessAttendant]);

    return (
        <Box className="bg-white border-r border-att-gray-300">
            <aside className="w-full md:w-[241px]">
                <nav>
                    {navItems.map((group, groupId) => (
                        <React.Fragment key={groupId}>
                            <ul key={groupId}>
                                {group.map((item, itemId) => (
                                    <li
                                        key={itemId}
                                        data-testid={item.testId + "-li"}
                                        className={clsx(
                                            "flex mx-2 my-1 h-11",
                                            {
                                                "hover:bg-att-hover-blue": getActiveNavText() !== item.label,
                                            },
                                            {
                                                "bg-att-cobalt-blue rounded-[4px] text-white": getActiveNavText() === item.label,
                                            },
                                        )}>
                                        <Link
                                            href={item.href}
                                            data-testid={item.testId}
                                            className={clsx(
                                                "px-4 py-2 h-full font-medium flex text-base w-full",
                                                {
                                                    "text-black": getActiveNavText() !== item.label,
                                                },
                                                {
                                                    "text-white": getActiveNavText() === item.label,
                                                },
                                            )}>
                                            {item.icon}
                                            <Text className="ml-3">{item.label}</Text>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                            <Separator
                                orientation="horizontal"
                                size="4"
                                className={clsx("my-[16px]", {
                                    "my-[16px] hidden": groupId + 1 === navItems.length,
                                })}
                            />
                        </React.Fragment>
                    ))}
                </nav>
            </aside>
        </Box>
    );
}
